import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Form, Row, Col, Alert, Spinner, Tabs, Tab, Collapse, Card,
} from 'react-bootstrap';
import ServiceOfferService from '../../../Services/ServiceOfferService';
import UserService from '../../../Services/UserService';
import ServiceTableEditor from './ServiceTableEditor';
import InclusionExclusionEditor from './InclusionExclusionEditor';
import AIExtractPanel from './AIExtractPanel';
import PdfPreviewModal from './PdfPreviewModal';
import OperatorQuotesPanel from './OperatorQuotesPanel';
import HtmlEditor from './HtmlEditor';
import EmailInboxPanel from '../../../Components/EmailInboxPanel';
import InclusionTemplateModal from './InclusionTemplateModal';

const DEFAULT_INCLUSIONS = [
  'VAT',
  'Professional driver service within agreed working hours',
  'Road tolls and taxes',
];

const DEFAULT_EXCLUSIONS = [
  'Parking fees',
  'Any required entrance permits',
  "For multi-day trips: driver's accommodation and half-board during the program (can be arranged by you or provided at an equivalent rate of EUR 130 per day)",
  "Driver's gratuity – EUR 2 per passenger per day",
  'Bottled water for passengers upon request – EUR 1.50 per bottle',
];

const DEFAULT_PAYMENT_TERMS = '<p>A 30% deposit is required at the time of booking to confirm the reservation.</p><p>The remaining 70% is due no later than 72 hours before the scheduled service. Failure to pay the balance by the 72-hour deadline will be treated as a cancellation under the "7 days or fewer" tier, and the full cost (100%) will be charged.</p><p>For bookings made fewer than 7 days before the scheduled service, full payment is required at the time of booking.</p><p>Payments can be made via bank transfer, credit card, or other methods agreed upon at the time of booking.</p><p>All prices are quoted in EUR and include VAT where applicable.</p>';
const DEFAULT_CANCELLATION_TERMS = '<ul><li>21 days or more before the scheduled service – no cancellation fee</li><li>14–20 days before the scheduled service – 50% of the total cost</li><li>8–13 days before the scheduled service – 75% of the total cost</li><li>7 days or fewer / no-show – 100% of the total cost</li></ul>';
const DEFAULT_CREATOR_TITLE = 'Representative of Pickup Hungary';

// Peak season: May–September (months 5–9) → 7 days; otherwise → 14 days
const getDefaultValidUntil = (dateStr) => {
  const base = dateStr ? new Date(dateStr) : new Date();
  const month = base.getMonth() + 1;
  const days = month >= 5 && month <= 9 ? 7 : 14;
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return result.toISOString().split('T')[0];
};

const getDaysFromOfferDate = (offerDate, validUntil) => {
  if (!offerDate || !validUntil) return null;
  const diff = Math.round((new Date(validUntil) - new Date(offerDate)) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

const LEAD_TABS = ['lead', 'operators', 'tables', 'details', 'inclusions', 'quote'];
const OFFER_TABS = ['details', 'tables', 'inclusions', 'quote', 'lead', 'operators'];

function ServiceOfferInputModal({
  show, onHide, offer, onSuccess, initialMode,
}) {
  const isEditing = offer && offer.Id;
  const isLead = initialMode === 'lead' || offer?.status === 'lead' || offer?.status === 'quoting';
  const sessionName = UserService.getInstance().getFullName();
  const sessionEmail = UserService.getInstance().getEmail();

  const [formData, setFormData] = useState({
    Id: '',
    title: '',
    recipient: {
      name: '', title: '', company: '', email: '', phone: '',
    },
    creator: { name: sessionName, title: DEFAULT_CREATOR_TITLE, email: sessionEmail },
    offerDate: new Date().toISOString().split('T')[0],
    validUntil: getDefaultValidUntil(new Date().toISOString().split('T')[0]),
    serviceTables: [{
      title: '',
      subtitle: '',
      columns: ['Vehicle Type', 'Rate'],
      rows: [{ cells: ['', ''] }],
      totals: [],
    }],
    inclusions: DEFAULT_INCLUSIONS,
    exclusions: DEFAULT_EXCLUSIONS,
    paymentTerms: DEFAULT_PAYMENT_TERMS,
    cancellationTerms: DEFAULT_CANCELLATION_TERMS,
    additionalNotes: '',
    leadText: '',
    operatorInquiryText: '',
    inquiryData: {
      summary: {
        dateRange: '', vehicleType: '', passengers: '', hoursPerDay: '',
      },
      days: [{ day: 1, date: '', itinerary: '' }],
      specialRequirements: [],
      additionalNotes: '',
    },
    referenceCode: '',
    status: initialMode === 'lead' ? 'lead' : 'draft',
  });

  const [activeTab, setActiveTab] = useState(isLead ? 'lead' : 'details');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [showExtractedFields, setShowExtractedFields] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copyingQuoteEmail, setCopyingQuoteEmail] = useState(false);
  const [quoteEmailHtml, setQuoteEmailHtml] = useState('');
  const [showQuoteEmailPreview, setShowQuoteEmailPreview] = useState(false);
  const extractedRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      setFormData({
        Id: offer.Id,
        title: offer.title || '',
        recipient: {
          name: offer.recipient?.name || '',
          title: offer.recipient?.title || '',
          company: offer.recipient?.company || '',
          email: offer.recipient?.email || '',
          phone: offer.recipient?.phone || '',
        },
        creator: {
          name: offer.creator?.name || sessionName,
          title: offer.creator?.title || DEFAULT_CREATOR_TITLE,
          email: offer.creator?.email || sessionEmail,
        },
        offerDate: offer.offerDate || new Date().toISOString().split('T')[0],
        validUntil: offer.validUntil || '',
        serviceTables: offer.serviceTables || [{
          title: '',
          subtitle: '',
          columns: ['Vehicle Type', 'Rate'],
          rows: [{ cells: ['', ''] }],
          totals: [],
        }],
        inclusions: offer.inclusions || DEFAULT_INCLUSIONS,
        exclusions: offer.exclusions || DEFAULT_EXCLUSIONS,
        paymentTerms: offer.paymentTerms || '',
        cancellationTerms: offer.cancellationTerms || DEFAULT_CANCELLATION_TERMS,
        additionalNotes: offer.additionalNotes || '',
        leadText: offer.leadText || '',
        operatorInquiryText: offer.operatorInquiryText || '',
        inquiryData: offer.inquiryData || {
          summary: {
            dateRange: '', vehicleType: '', passengers: '', hoursPerDay: '',
          },
          days: [{ day: 1, date: '', itinerary: '' }],
          specialRequirements: [],
          additionalNotes: '',
        },
        referenceCode: offer.referenceCode || '',
        status: offer.status || 'draft',
      });
      // Show extracted fields immediately when editing existing lead with data
      const hasData = offer.inquiryData?.days?.some((d) => d.itinerary)
        || offer.recipient?.name
        || offer.leadText;
      if (hasData) setShowExtractedFields(true);
    }
  }, [offer]);

  const fetchTemplates = async () => {
    try {
      const response = await ServiceOfferService.fetchTemplates();
      setTemplates(response?.data?.data || []);
    } catch {
      // silently fail — templates are optional
    }
  };

  useEffect(() => {
    if (show) fetchTemplates();
  }, [show]);

  const updateField = (field, value) => {
    setFormData((prev) => {
      const updates = { ...prev, [field]: value };
      if (field === 'offerDate' && value) {
        updates.validUntil = getDefaultValidUntil(value);
      }
      return updates;
    });
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) return;
    const tpl = templates.find((t) => t.Id === templateId);
    if (tpl) {
      updateField('inclusions', [...tpl.inclusions]);
      updateField('exclusions', [...tpl.exclusions]);
    }
  };

  const updateRecipient = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      recipient: { ...prev.recipient, [field]: value },
    }));
  };

  const updateCreator = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      creator: { ...prev.creator, [field]: value },
    }));
  };

  const updateInquirySummary = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      inquiryData: {
        ...prev.inquiryData,
        summary: { ...prev.inquiryData.summary, [field]: value },
      },
    }));
  };

  const updateInquiryDay = (index, field, value) => {
    setFormData((prev) => {
      const days = [...prev.inquiryData.days];
      days[index] = { ...days[index], [field]: value };
      return { ...prev, inquiryData: { ...prev.inquiryData, days } };
    });
  };

  const addInquiryDay = () => {
    setFormData((prev) => {
      const days = [...prev.inquiryData.days];
      days.push({ day: days.length + 1, date: '', itinerary: '' });
      return { ...prev, inquiryData: { ...prev.inquiryData, days } };
    });
  };

  const removeInquiryDay = (index) => {
    setFormData((prev) => {
      const days = prev.inquiryData.days
        .filter((_, i) => i !== index)
        .map((d, i) => ({ ...d, day: i + 1 }));
      return { ...prev, inquiryData: { ...prev.inquiryData, days } };
    });
  };

  const addInquiryRequirement = () => {
    setFormData((prev) => ({
      ...prev,
      inquiryData: {
        ...prev.inquiryData,
        specialRequirements: [...(prev.inquiryData.specialRequirements || []), ''],
      },
    }));
  };

  const removeInquiryRequirement = (index) => {
    setFormData((prev) => ({
      ...prev,
      inquiryData: {
        ...prev.inquiryData,
        specialRequirements: prev.inquiryData.specialRequirements.filter((_, i) => i !== index),
      },
    }));
  };

  const updateInquiryRequirement = (index, value) => {
    setFormData((prev) => {
      const reqs = [...prev.inquiryData.specialRequirements];
      reqs[index] = value;
      return { ...prev, inquiryData: { ...prev.inquiryData, specialRequirements: reqs } };
    });
  };

  const handleApplyExtraction = (extractedOffer) => {
    setFormData((prev) => ({
      ...prev,
      title: extractedOffer.title || prev.title,
      recipient: {
        name: extractedOffer.recipient?.name || prev.recipient.name,
        title: extractedOffer.recipient?.title || prev.recipient.title,
        company: extractedOffer.recipient?.company || prev.recipient.company,
        email: extractedOffer.recipient?.email || prev.recipient.email,
        phone: extractedOffer.recipient?.phone || prev.recipient.phone,
      },
      creator: {
        name: extractedOffer.creator?.name || prev.creator.name,
        title: DEFAULT_CREATOR_TITLE,
      },
      offerDate: extractedOffer.offerDate || prev.offerDate,
      validUntil: extractedOffer.validUntil || prev.validUntil,
      serviceTables: extractedOffer.serviceTables?.length > 0
        ? extractedOffer.serviceTables
        : prev.serviceTables,
      inclusions: extractedOffer.inclusions?.length > 0
        ? extractedOffer.inclusions
        : prev.inclusions,
      exclusions: extractedOffer.exclusions?.length > 0
        ? extractedOffer.exclusions
        : prev.exclusions,
      paymentTerms: extractedOffer.paymentTerms || prev.paymentTerms,
      cancellationTerms: extractedOffer.cancellationTerms || prev.cancellationTerms,
      additionalNotes: extractedOffer.additionalNotes || prev.additionalNotes,
    }));
  };

  const handleFormatInquiry = async () => {
    const text = formData.leadText;
    if (!text || text.length < 20) return;
    setFormatting(true);
    setError('');
    try {
      const response = await ServiceOfferService.formatInquiry(text);
      const data = response?.data?.data || response?.data || {};
      if (data.success && data.inquiryData) {
        setFormData((prev) => ({
          ...prev,
          inquiryData: data.inquiryData,
          title: (data.title && !prev.title) ? data.title : prev.title,
          recipient: data.client ? {
            name: data.client.name || prev.recipient.name,
            title: data.client.title || prev.recipient.title,
            company: data.client.company || prev.recipient.company,
            email: data.client.email || prev.recipient.email,
            phone: data.client.phone || prev.recipient.phone,
          } : prev.recipient,
        }));
        setShowExtractedFields(true);
        // Scroll to extracted fields after reveal
        setTimeout(() => {
          extractedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setError('Failed to format inquiry');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to format inquiry');
    } finally {
      setFormatting(false);
    }
  };

  const reloadOffer = async () => {
    if (!formData.Id) return;
    try {
      const response = await ServiceOfferService.fetchById(formData.Id);
      const updated = response?.data?.data || response?.data;
      if (updated) {
        setFormData((prev) => ({
          ...prev,
          serviceTables: updated.serviceTables || prev.serviceTables,
          inclusions: updated.inclusions || prev.inclusions,
          exclusions: updated.exclusions || prev.exclusions,
          paymentTerms: updated.paymentTerms || prev.paymentTerms,
          cancellationTerms: updated.cancellationTerms || prev.cancellationTerms,
          additionalNotes: updated.additionalNotes || prev.additionalNotes,
          status: updated.status || prev.status,
        }));
      }
    } catch {
      // Silently fail
    }
  };

  const autoSaveLead = async () => {
    setAutoSaving(true);
    setError('');
    try {
      const payload = { ...formData };
      if (!payload.Id) {
        delete payload.Id;
      }
      if (!payload.title) {
        const defaultTitle = `Lead – ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        payload.title = defaultTitle;
        updateField('title', defaultTitle);
      }
      const response = await ServiceOfferService.createOrUpdate(payload);
      const saved = response?.data?.data || response?.data;
      if (saved?.Id) {
        setFormData((prev) => ({
          ...prev,
          Id: saved.Id,
          referenceCode: saved.referenceCode || prev.referenceCode,
        }));
      }
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to auto-save lead');
      return false;
    } finally {
      setAutoSaving(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      const payload = { ...formData };
      if (!isEditing) {
        delete payload.Id;
      }

      await ServiceOfferService.createOrUpdate(payload);
      onSuccess();
      onHide();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save service offer');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPdf = async () => {
    setPreviewLoading(true);
    setError('');

    try {
      const response = await ServiceOfferService.previewPdf(formData, selectedLanguage);
      const pdfData = response?.data?.data;
      if (pdfData?.pdf) {
        setPreviewPdf(pdfData);
      } else {
        setError('Failed to generate PDF preview');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate PDF preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await ServiceOfferService.previewPdf(formData, selectedLanguage);
      const pdfData = response?.data?.data;
      if (pdfData?.pdf) {
        ServiceOfferService.downloadPdf(pdfData);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download PDF', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const buildQuoteEmailHtml = () => {
    const language = selectedLanguage;

    // Email translations
    const getGreetingEn = (name) => (name ? `Dear ${name},` : 'Dear Sir/Madam,');
    const getGreetingRu = (name) => (name ? `Уважаемый(ая) ${name},` : 'Уважаемый клиент,');

    const translations = {
      en: {
        inclusions: 'Inclusions',
        exclusions: 'Exclusions',
        paymentTerms: 'Payment Terms',
        cancellationTerms: 'Cancellation Terms',
        additionalNotes: 'Additional Notes',
        validUntil: 'This offer is valid until',
        greeting: getGreetingEn,
        intro: 'Please find our service offer details below. The full offer is attached as a PDF.',
        serviceOffer: 'Service Offer',
        kindRegards: 'Kind regards',
      },
      ru: {
        inclusions: 'Включено',
        exclusions: 'Не Включено',
        paymentTerms: 'Условия Оплаты',
        cancellationTerms: 'Условия Отмены',
        additionalNotes: 'Дополнительные Примечания',
        validUntil: 'Это предложение действительно до',
        greeting: getGreetingRu,
        intro: 'Пожалуйста, ознакомьтесь с деталями нашего предложения ниже. Полная версия предложения прикреплена в виде PDF.',
        serviceOffer: 'Коммерческое Предложение',
        kindRegards: 'С уважением',
      },
    };

    const t = translations[language] || translations.en;

    const fmt = (dateStr) => {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
        });
      } catch { return dateStr; }
    };

    const tableHtml = (formData.serviceTables || []).map((table) => {
      if (!table.title && !table.rows?.length) return '';
      const colCount = table.columns?.length || 1;
      const headerCells = (table.columns || []).map((c) => `<th style="padding:8px 12px;text-align:left;font-size:12px;letter-spacing:0.5px;text-transform:uppercase;">${c}</th>`).join('');
      const dataRows = (table.rows || []).map((r, ri) => `<tr style="background:${ri % 2 === 0 ? '#f8f9fa' : '#ffffff'};">${(r.cells || []).map((cell, ci) => `<td style="padding:8px 12px;border-top:1px solid #e9ecef;font-size:13px;${ci === 0 ? 'font-weight:600;' : ''}">${cell}</td>`).join('')}</tr>`).join('');
      const totalRows = (table.totals || []).map((total, ti) => `<tr style="background:#f8f9fa;border-top:${ti === 0 ? '2px solid #343a40' : '1px solid #e9ecef'};"><td colspan="${colCount - 1}" style="padding:8px 12px;font-weight:700;font-size:13px;">${total.label}</td><td style="padding:8px 12px;font-weight:700;font-size:13px;">${total.value}</td></tr>`).join('');
      return `${table.title ? `<h3 style="font-size:14px;font-weight:700;margin:20px 0 4px;">${table.title}</h3>` : ''}${table.subtitle ? `<p style="font-size:12px;color:#6c757d;font-style:italic;margin:0 0 8px;">${table.subtitle}</p>` : ''}<table style="width:100%;border-collapse:collapse;border:1px solid #dee2e6;margin-bottom:16px;"><thead><tr style="background:#343a40;color:#ffffff;">${headerCells}</tr></thead><tbody>${dataRows}${totalRows}</tbody></table>`;
    }).join('');

    const inclusionsHtml = formData.inclusions?.length
      ? `<h3 style="font-size:13px;font-weight:700;margin:20px 0 6px;">${t.inclusions}</h3><ul style="margin:0 0 16px;padding-left:20px;">${formData.inclusions.map((i) => `<li style="font-size:13px;margin-bottom:3px;">${i}</li>`).join('')}</ul>`
      : '';
    const exclusionsHtml = formData.exclusions?.length
      ? `<h3 style="font-size:13px;font-weight:700;margin:20px 0 6px;">${t.exclusions}</h3><ul style="margin:0 0 16px;padding-left:20px;">${formData.exclusions.map((e) => `<li style="font-size:13px;margin-bottom:3px;color:#6c757d;">${e}</li>`).join('')}</ul>`
      : '';
    const paymentHtml = formData.paymentTerms
      ? `<h3 style="font-size:13px;font-weight:700;margin:20px 0 6px;">${t.paymentTerms}</h3><div style="font-size:13px;margin:0 0 16px;line-height:1.5;">${formData.paymentTerms}</div>`
      : '';
    const cancellationHtml = formData.cancellationTerms
      ? `<h3 style="font-size:13px;font-weight:700;margin:20px 0 6px;">${t.cancellationTerms}</h3><div style="font-size:13px;margin:0 0 16px;line-height:1.5;">${formData.cancellationTerms}</div>`
      : '';
    const notesHtml = formData.additionalNotes
      ? `<h3 style="font-size:13px;font-weight:700;margin:20px 0 6px;">${t.additionalNotes}</h3><div style="font-size:13px;margin:0 0 16px;line-height:1.5;">${formData.additionalNotes}</div>`
      : '';
    const validityHtml = formData.validUntil
      ? `<p style="font-size:13px;color:#6c757d;margin:0 0 16px;">${t.validUntil} <strong>${fmt(formData.validUntil)}</strong>.</p>`
      : '';

    return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="font-family:Arial,sans-serif;font-size:14px;color:#333;max-width:700px;margin:0;padding:0;line-height:1.6;"><p style="margin:0 0 16px;">${t.greeting(formData.recipient?.name)}</p><p style="margin:0 0 16px;">${t.intro}. The full offer is attached as a PDF.</p><h2 style="font-size:16px;font-weight:700;margin:24px 0 16px;padding-bottom:8px;border-bottom:2px solid #e9ecef;">${formData.title || t.serviceOffer}</h2>${tableHtml}${inclusionsHtml}${exclusionsHtml}${paymentHtml}${cancellationHtml}${notesHtml}${validityHtml}<div style="margin-top:32px;padding-top:16px;border-top:1px solid #e9ecef;font-size:13px;"><p style="margin:0;">${t.kindRegards},</p><p style="margin:4px 0 0;font-weight:700;">${formData.creator?.name || ''}</p><p style="margin:2px 0 0;color:#6c757d;">${formData.creator?.title || ''}</p><p style="margin:6px 0 0;color:#6c757d;">Pickup Hungary &nbsp;|&nbsp; +36 70 717 7656 &nbsp;|&nbsp; info@pickuphungary.com</p></div></body></html>`;
  };

  const handleCopyQuoteEmail = async () => {
    setCopyingQuoteEmail(true);
    try {
      const html = buildQuoteEmailHtml();
      setQuoteEmailHtml(html);
      const htmlBlob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([html], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
      ]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy email', err);
    } finally {
      setCopyingQuoteEmail(false);
    }
  };

  const handlePreviewQuoteEmail = () => {
    setQuoteEmailHtml(buildQuoteEmailHtml());
    setShowQuoteEmailPreview((prev) => !prev);
  };

  const tabOrder = isLead ? LEAD_TABS : OFFER_TABS;

  const navigateToTab = async (tabKey) => {
    // Auto-save lead data when leaving the Lead tab or going to Operators
    if (isLead && (activeTab === 'lead' || tabKey === 'operators')) {
      const saved = await autoSaveLead();
      if (!saved) return;
    }
    setActiveTab(tabKey);
  };

  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      navigateToTab(tabOrder[currentIndex + 1]);
    }
  };

  const getTabTitle = (key) => {
    if (!isLead) {
      const titles = {
        details: 'Details',
        tables: 'Service Tables',
        inclusions: 'Inclusions & Terms',
        quote: 'Quote',
        lead: 'Lead',
        operators: 'Operators',
      };
      return titles[key] || key;
    }

    const step = tabOrder.indexOf(key) + 1;
    const titles = {
      lead: `${step}. Lead Info`,
      operators: `${step}. Operators`,
      tables: `${step}. Service Tables`,
      details: `${step}. Details`,
      inclusions: `${step}. Terms`,
      quote: `${step}. Quote`,
    };
    return titles[key] || key;
  };

  const renderNextButton = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex >= tabOrder.length - 1) return null;
    return (
      <div className="d-flex justify-content-end mt-3">
        <Button variant="outline-primary" size="sm" onClick={goToNextTab}>
          Next Step
          <i className="fa fa-arrow-right ms-2" />
        </Button>
      </div>
    );
  };

  /* eslint-disable no-nested-ternary */
  const modalTitle = isEditing
    ? (isLead ? 'Edit Lead' : 'Edit Service Offer')
    : (isLead ? 'New Lead' : 'New Service Offer');
  /* eslint-enable no-nested-ternary */

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`fa ${isLead ? 'fa-bullhorn' : 'fa-file-invoice'} me-2`} />
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* AI Extract Panel */}
          {!isEditing && !isLead && (
            <AIExtractPanel onApplyExtraction={handleApplyExtraction} />
          )}

          <Tabs activeKey={activeTab} onSelect={(k) => navigateToTab(k)} className="mb-3">
            {/* Lead Info Tab */}
            <Tab eventKey="lead" title={getTabTitle('lead')}>
              {/* Inline keyframes for AI animations */}
              <style>
                {`
                  @keyframes aiPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
                    50% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
                  }
                  @keyframes aiSpin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  @keyframes revealSlide {
                    from { opacity: 0; transform: translateY(-12px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                  .ai-extracted-reveal {
                    animation: revealSlide 0.4s ease-out forwards;
                  }
                `}
              </style>

              {/* Client request — tabbed: Paste vs Inbox */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="fw-semibold mb-0">
                  <i className="fa fa-envelope-open me-1" />
                  Client Request
                </Form.Label>
                <div
                  className="d-inline-flex rounded-pill"
                  style={{ background: '#f0f0f0', padding: '2px' }}
                >
                  <button
                    type="button"
                    className="border-0 rounded-pill px-3 py-1 small"
                    style={{
                      background: !showInbox ? '#fff' : 'transparent',
                      boxShadow: !showInbox ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      fontWeight: !showInbox ? 600 : 400,
                      color: !showInbox ? '#333' : '#888',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setShowInbox(false)}
                  >
                    <i className="fa fa-paste me-1" />
                    Paste
                  </button>
                  <button
                    type="button"
                    className="border-0 rounded-pill px-3 py-1 small"
                    style={{
                      background: showInbox ? '#fff' : 'transparent',
                      boxShadow: showInbox ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      fontWeight: showInbox ? 600 : 400,
                      color: showInbox ? '#333' : '#888',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => setShowInbox(true)}
                  >
                    <i className="fa fa-inbox me-1" />
                    Inbox
                  </button>
                </div>
              </div>
              <div className="mb-3">
                {showInbox ? (
                  <EmailInboxPanel onSelect={(html) => { updateField('leadText', html); setShowInbox(false); }} />
                ) : (
                  <HtmlEditor
                    value={formData.leadText}
                    onChange={(html) => updateField('leadText', html)}
                    placeholder="Paste the client email here..."
                  />
                )}
              </div>

              {/* AI Extract action bar */}
              <div
                className="d-flex align-items-center mb-4 px-3 py-2 rounded"
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center me-3"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff',
                    fontSize: '16px',
                    flexShrink: 0,
                    animation: formatting ? 'aiPulse 1.2s ease-in-out infinite' : 'none',
                  }}
                >
                  <i
                    className="fa fa-magic"
                    style={{
                      animation: formatting ? 'aiSpin 1.5s linear infinite' : 'none',
                    }}
                  />
                </div>
                <div className="me-auto">
                  <div className="fw-semibold" style={{ fontSize: '13px', lineHeight: 1.3 }}>
                    AI Extract
                  </div>
                  <div className="text-muted" style={{ fontSize: '12px', lineHeight: 1.3 }}>
                    Fills title, client details, itinerary &amp; requirements
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleFormatInquiry}
                  disabled={formatting || !formData.leadText || formData.leadText.length < 20}
                  style={{ minWidth: '70px' }}
                >
                  {formatting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Running...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-play me-1" />
                      Run
                    </>
                  )}
                </Button>
              </div>

              {/* Wizard gate: show fields only after extraction or manual skip */}
              {!showExtractedFields ? (
                <div className="text-center py-4" style={{ color: '#6c757d' }}>
                  <p className="mb-2">
                    Paste the client email above and click
                    {' '}
                    <strong>Run</strong>
                    {' '}
                    to auto-fill all fields below.
                  </p>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-muted"
                    onClick={() => setShowExtractedFields(true)}
                  >
                    or fill in manually
                  </Button>
                </div>
              ) : (
                <div ref={extractedRef} className="ai-extracted-reveal">
                  {/* Structured Operator Inquiry */}
                  <h6 className="fw-bold mt-2 mb-3">
                    <i className="fa fa-paper-plane me-2 text-primary" />
                    Operator Inquiry
                    <small className="text-muted fw-normal ms-2">
                      (sent to operators)
                    </small>
                  </h6>

                  {/* Summary */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Date Range</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={formData.inquiryData.summary?.dateRange || ''}
                          onChange={(e) => updateInquirySummary('dateRange', e.target.value)}
                          placeholder="e.g. 31 Oct – 01 Nov"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Vehicle</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={formData.inquiryData.summary?.vehicleType || ''}
                          onChange={(e) => updateInquirySummary('vehicleType', e.target.value)}
                          placeholder="e.g. Minibus"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Passengers</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={formData.inquiryData.summary?.passengers || ''}
                          onChange={(e) => updateInquirySummary('passengers', e.target.value)}
                          placeholder="e.g. 16 pax"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Hours/Day</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={formData.inquiryData.summary?.hoursPerDay || ''}
                          onChange={(e) => updateInquirySummary('hoursPerDay', e.target.value)}
                          placeholder="e.g. 10 hours"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Itinerary Days */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Form.Label className="fw-semibold mb-0">Itinerary</Form.Label>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={addInquiryDay}
                      >
                        <i className="fa fa-plus me-1" />
                        Add Day
                      </Button>
                    </div>
                    {(formData.inquiryData.days || []).map((day, idx) => (
                      <Row className="mb-2 g-2 align-items-start" key={`day-${day.day}`}>
                        <Col xs="auto" style={{ width: '45px' }}>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={day.day}
                            disabled
                            className="text-center"
                          />
                        </Col>
                        <Col xs="auto" style={{ width: '100px' }}>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={day.date}
                            onChange={(e) => updateInquiryDay(idx, 'date', e.target.value)}
                            placeholder="DD-Mon"
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            size="sm"
                            as="textarea"
                            rows={3}
                            value={day.itinerary}
                            onChange={(e) => updateInquiryDay(idx, 'itinerary', e.target.value)}
                            placeholder="Route and schedule details..."
                            style={{ resize: 'vertical', minHeight: '60px' }}
                          />
                        </Col>
                        <Col xs="auto">
                          {formData.inquiryData.days.length > 1 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeInquiryDay(idx)}
                            >
                              <i className="fa fa-times" />
                            </Button>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </div>

                  {/* Special Requirements */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Form.Label className="fw-semibold mb-0">
                        Special Requirements
                      </Form.Label>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={addInquiryRequirement}
                      >
                        <i className="fa fa-plus me-1" />
                        Add Requirement
                      </Button>
                    </div>
                    {(formData.inquiryData.specialRequirements || []).map((req, idx) => (
                      <Row className="mb-2 g-2" key={`req-${idx + 1}`}>
                        <Col>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={req}
                            onChange={(e) => updateInquiryRequirement(idx, e.target.value)}
                            placeholder="e.g. 16 suitcases + hand luggage"
                          />
                        </Col>
                        <Col xs="auto">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeInquiryRequirement(idx)}
                          >
                            <i className="fa fa-times" />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                  </div>

                  {/* Additional Notes */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Additional Notes</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={formData.inquiryData.additionalNotes || ''}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        inquiryData: { ...prev.inquiryData, additionalNotes: e.target.value },
                      }))}
                      placeholder="Any additional context..."
                    />
                  </Form.Group>

                  {formData.referenceCode && (
                    <Alert variant="info">
                      <strong>Reference Code:</strong>
                      {' '}
                      {formData.referenceCode}
                    </Alert>
                  )}

                  {/* Client Details */}
                  <h6 className="fw-bold mt-4 mb-3">
                    <i className="fa fa-user me-2 text-primary" />
                    Client Details
                  </h6>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.recipient.name}
                          onChange={(e) => updateRecipient('name', e.target.value)}
                          placeholder="e.g. Kevin Decerle"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.recipient.title}
                          onChange={(e) => updateRecipient('title', e.target.value)}
                          placeholder="e.g. Project Manager"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Company</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.recipient.company}
                          onChange={(e) => updateRecipient('company', e.target.value)}
                          placeholder="e.g. VPI Agency"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.recipient.email}
                          onChange={(e) => updateRecipient('email', e.target.value)}
                          placeholder="email@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.recipient.phone}
                          onChange={(e) => updateRecipient('phone', e.target.value)}
                          placeholder="+36 ..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {isLead && renderNextButton()}
            </Tab>

            {/* Operators Tab */}
            <Tab
              eventKey="operators"
              title={getTabTitle('operators')}
              disabled={!formData.Id && !autoSaving}
            >
              {autoSaving ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving lead...
                </div>
              ) : (
                <OperatorQuotesPanel
                  serviceOfferId={formData.Id}
                  onOfferUpdated={reloadOffer}
                />
              )}
              {isLead && renderNextButton()}
            </Tab>

            {/* Service Tables Tab */}
            <Tab eventKey="tables" title={getTabTitle('tables')}>
              <ServiceTableEditor
                tables={formData.serviceTables}
                onChange={(tables) => updateField('serviceTables', tables)}
              />
              {isLead && renderNextButton()}
            </Tab>

            {/* Details Tab */}
            <Tab eventKey="details" title={getTabTitle('details')}>
              {/* Basic Info */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Offer Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={(e) => updateField('title', e.target.value)}
                      placeholder="e.g. Transportation Service Offer – Budapest (UEFA Finals – May 29–31, 2026)"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Offer Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.offerDate}
                      onChange={(e) => updateField('offerDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Valid Until</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.validUntil}
                      onChange={(e) => updateField('validUntil', e.target.value)}
                    />
                    {getDaysFromOfferDate(formData.offerDate, formData.validUntil) && (
                      <Form.Text className="text-muted">
                        {
                        getDaysFromOfferDate(
                          formData.offerDate,
                          formData.validUntil,
                        )
}
                        {' '}
                        days from offer date
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} />
              </Row>

              {/* Recipient */}
              <h6 className="fw-bold mt-4 mb-3">
                <i className="fa fa-user me-2 text-primary" />
                Recipient
              </h6>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.recipient.name}
                      onChange={(e) => updateRecipient('name', e.target.value)}
                      placeholder="e.g. Kevin Decerle"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.recipient.title}
                      onChange={(e) => updateRecipient('title', e.target.value)}
                      placeholder="e.g. Project Manager"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.recipient.company}
                      onChange={(e) => updateRecipient('company', e.target.value)}
                      placeholder="e.g. VPI Agency"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.recipient.email}
                      onChange={(e) => updateRecipient('email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.recipient.phone}
                      onChange={(e) => updateRecipient('phone', e.target.value)}
                      placeholder="+36 ..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Creator */}
              <h6 className="fw-bold mt-4 mb-3">
                <i className="fa fa-user-tie me-2 text-primary" />
                Created By
              </h6>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.creator.name}
                      onChange={(e) => updateCreator('name', e.target.value)}
                      placeholder="e.g. Anna Kravinskaya"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.creator.title}
                      readOnly
                      plaintext={false}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              {isLead && renderNextButton()}
            </Tab>

            {/* Inclusions & Terms Tab */}
            <Tab eventKey="inclusions" title={getTabTitle('inclusions')}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Form.Select
                  size="sm"
                  style={{ maxWidth: '280px' }}
                  defaultValue=""
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                >
                  <option value="">Apply template...</option>
                  {templates.map((t) => (
                    <option key={t.Id} value={t.Id}>{t.name}</option>
                  ))}
                </Form.Select>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  onClick={() => setShowTemplateModal(true)}
                >
                  <i className="fa fa-cog me-1" />
                  Manage
                </Button>
              </div>

              <InclusionExclusionEditor
                items={formData.inclusions}
                onChange={(items) => updateField('inclusions', items)}
                label="Inclusions"
                placeholder="Select or type to add..."
                defaultOptions={DEFAULT_INCLUSIONS}
              />

              <InclusionExclusionEditor
                items={formData.exclusions}
                onChange={(items) => updateField('exclusions', items)}
                label="Exclusions"
                placeholder="Select or type to add..."
                defaultOptions={DEFAULT_EXCLUSIONS}
              />

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Payment Terms</Form.Label>
                <HtmlEditor
                  value={formData.paymentTerms}
                  onChange={(html) => updateField('paymentTerms', html)}
                  placeholder="e.g. Advance payment required..."
                  style={{ minHeight: '150px' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Cancellation Terms</Form.Label>
                <HtmlEditor
                  value={formData.cancellationTerms}
                  onChange={(html) => updateField('cancellationTerms', html)}
                  placeholder="e.g. Free cancellation until April 20, 2026."
                  style={{ minHeight: '120px' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Additional Notes</Form.Label>
                <HtmlEditor
                  value={formData.additionalNotes}
                  onChange={(html) => updateField('additionalNotes', html)}
                  placeholder="Any additional notes..."
                  style={{ minHeight: '100px' }}
                />
              </Form.Group>
            </Tab>

            {/* Quote Tab */}
            <Tab eventKey="quote" title={getTabTitle('quote')}>
              {/* Status and Language */}
              <div className="d-flex gap-4 mb-4">
                <Form.Group>
                  <Form.Label className="fw-semibold">Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => updateField('status', e.target.value)}
                    style={{ maxWidth: '220px' }}
                  >
                    <option value="lead">Lead</option>
                    <option value="quoting">Quoting</option>
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="expired">Expired</option>
                    <option value="rejected">Rejected</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label className="fw-semibold">Language</Form.Label>
                  <Form.Select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    style={{ maxWidth: '150px' }}
                  >
                    <option value="en">English</option>
                    <option value="ru">Русский</option>
                  </Form.Select>
                </Form.Group>
              </div>

              {/* PDF */}
              <h6 className="fw-bold mb-3">
                <i className="fa fa-file-pdf-o me-2 text-danger" />
                PDF
              </h6>
              <div className="d-flex gap-2 mb-4">
                <Button
                  variant="outline-secondary"
                  onClick={handlePreviewPdf}
                  disabled={previewLoading || !formData.title}
                >
                  {previewLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-eye me-2" />
                      Preview
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || !formData.title}
                >
                  {downloadingPdf ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-download me-2" />
                      Download PDF
                    </>
                  )}
                </Button>
              </div>

              {/* Email to client */}
              <h6 className="fw-bold mb-1">
                <i className="fa fa-envelope me-2 text-primary" />
                Send to Client
              </h6>
              <p className="text-muted small mb-3">
                Copy the quote as a formatted email, ready to paste into Gmail or Outlook.
              </p>
              <div className="d-flex gap-2 mb-3">
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={handlePreviewQuoteEmail}
                  disabled={!formData.title}
                >
                  <i className="fa fa-eye me-1" />
                  {showQuoteEmailPreview ? 'Hide Preview' : 'Preview Email'}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleCopyQuoteEmail}
                  disabled={copyingQuoteEmail || !formData.title}
                >
                  {copyingQuoteEmail ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Copying...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-clipboard me-1" />
                      Copy to Clipboard
                    </>
                  )}
                </Button>
              </div>
              <Collapse in={showQuoteEmailPreview}>
                <div>
                  <Card className="border-info">
                    <Card.Body style={{ padding: 0, maxHeight: '400px', overflow: 'auto' }}>
                      {/* eslint-disable-next-line react/no-danger */}
                      <div dangerouslySetInnerHTML={{ __html: quoteEmailHtml }} style={{ padding: '16px' }} />
                    </Card.Body>
                  </Card>
                </div>
              </Collapse>

              {isLead && renderNextButton()}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <div className="d-flex gap-2">
            <Button variant="secondary" onClick={onHide}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !formData.title || (!isLead && !formData.recipient.name)}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2" />
                  {isEditing ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* PDF Preview Modal */}
      {previewPdf && (
        <PdfPreviewModal
          show={!!previewPdf}
          onHide={() => setPreviewPdf(null)}
          pdfBase64={previewPdf.pdf}
          filename={previewPdf.filename}
        />
      )}

      {/* Inclusion Template Management Modal */}
      <InclusionTemplateModal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        onTemplatesChanged={fetchTemplates}
        currentInclusions={formData.inclusions}
        currentExclusions={formData.exclusions}
      />
    </>
  );
}

export default ServiceOfferInputModal;
